// Central logo config — reused across Header, HomePage, footer, cards.
// icons8 (color/96) works reliably as a hotlinkable CDN for major vendor logos.

const I = "https://img.icons8.com/color/96";
const CC = `${I}/adobe-creative-cloud.png`;

export const VENDOR_LOGOS = {
  microsoft: `${I}/microsoft.png`,
  microsoft365: `${I}/microsoft-office-2019.png`,
  windows: `${I}/windows-11.png`,
  office: `${I}/microsoft-office-2019.png`,
  sqlserver: `${I}/microsoft-sql-server.png`,
  azure: `${I}/azure-1.png`,
  adobe: `${I}/adobe-creative-cloud.png`,
  photoshop: `${I}/adobe-photoshop.png`,
  illustrator: `${I}/adobe-illustrator.png`,
  premierepro: `${I}/adobe-premiere-pro.png`,
  acrobat: `${I}/adobe-acrobat.png`,
  creativecloud: CC,
  autodesk: `${I}/autodesk.png`,
  autocad: "https://damassets.autodesk.net/content/dam/autodesk/www/product-imagery/badge-75x75/simplified-badges/autocad-2023-simplified-badge-75x75.png",
  revit: "https://damassets.autodesk.net/content/dam/autodesk/www/product-imagery/badge-75x75/simplified-badges/revit-2023-simplified-badge-75x75.png",
  fusion: "https://damassets.autodesk.net/content/dam/autodesk/www/product-imagery/badge-75x75/simplified-badges/fusion-360-2023-simplified-badge-75x75.png",
  maya: "https://damassets.autodesk.net/content/dam/autodesk/www/product-imagery/badge-75x75/simplified-badges/maya-2023-simplified-badge-75x75.png",
  aws: `${I}/amazon-web-services.png`,
  gcp: `${I}/google-cloud.png`,
  googleworkspace: `${I}/gmail.png`,
  gmail: `${I}/gmail.png`,
  cybersecurity: `${I}/cyber-security.png`,
  vpn: `${I}/vpn.png`,
  backup: `${I}/cloud-backup-restore.png`,
  helpdesk: `${I}/headset.png`,
  network: `${I}/network.png`,
  firewall: `${I}/firewall.png`,
  endpoint: `${I}/laptop.png`,
  emailmigration: `${I}/forward-arrow.png`,
  data: `${I}/database.png`,
  tally: `${I}/accounting.png`,
  corel: `${I}/coreldraw.png`,
  // Antivirus / endpoint security brands — Wikimedia hosts stable SVG marks.
  quickheal: "/brands/quick-heal.png",
  seqrite:   "/brands/seqrite.jpeg",
  chaos:     "/brands/chaos-logo.png",
  sketchup: "/brand-logos/sketchup.svg",
  dell: `${I}/dell.png`,
  hpe: "/brand-logos/hpe-tight.svg",
  zoho: "/brand-logos/zoho.svg",
};

// Mega-menu configuration — matches the Wroffy-style dropdown reference.
// Each column has a title + list of items with logo + label + link.
export const MEGA_MENU = {
  microsoft: {
    label: "Microsoft",
    logo: VENDOR_LOGOS.microsoft,
    href: "/microsoft",
    columns: [
      {
        title: "🔥 Hot Sellers",
        items: [
          { logo: VENDOR_LOGOS.microsoft, label: "All Microsoft Hot Sellers", href: "/microsoft" },
          { logo: VENDOR_LOGOS.windows, label: "Windows Server (16-Core)", href: "/microsoft#catalog" },
          { logo: VENDOR_LOGOS.sqlserver, label: "SQL Server 2022 / 2025", href: "/microsoft#catalog" },
          { logo: VENDOR_LOGOS.office, label: "Office LTSC 2021 / 2024", href: "/microsoft#catalog" },
        ],
      },
      {
        title: "Servers & Data Platform",
        items: [
          { logo: VENDOR_LOGOS.windows, label: "Windows Server 2025", href: "/products?category=Windows%20Server" },
          { logo: VENDOR_LOGOS.sqlserver, label: "SQL Server 2022 / 2025", href: "/products?category=SQL%20Server" },
          { logo: VENDOR_LOGOS.microsoft, label: "Exchange Server", href: "/products?category=Exchange%20Server" },
          { logo: VENDOR_LOGOS.microsoft, label: "SharePoint Server", href: "/products?category=SharePoint%20Server" },
        ],
      },
      {
        title: "Full Catalog",
        items: [
          { logo: VENDOR_LOGOS.windows, label: "Windows 11 Pro", href: "/products?category=Windows%20OS" },
          { logo: VENDOR_LOGOS.windows, label: "Windows Server", href: "/products?category=Windows%20Server" },
          { logo: VENDOR_LOGOS.office, label: "Office Home & Business", href: "/products?category=Microsoft%20Office" },
          { logo: VENDOR_LOGOS.microsoft, label: "Microsoft Project", href: "/products?category=Microsoft%20Project" },
        ],
      },
    ],
    footer: { label: "🔥 View Microsoft Hot Sellers Catalog →", href: "/microsoft" },
  },
  msSubscription: {
    label: "MS Subscription",
    logo: VENDOR_LOGOS.microsoft,
    href: "/microsoft-subscriptions",
    columns: [
      {
        title: "Microsoft 365 · Business",
        // 30 Aug 2026 — top-level nav entry now points at the dedicated
        // /microsoft-365-business landing page instead of the catalog
        // grid. Business page carries all Basic/Standard/Premium tiers
        // plus Copilot bundles + Teams Essentials + Apps for Business.
        items: [
          { logo: VENDOR_LOGOS.microsoft365, label: "Microsoft 365 Business Plans",  href: "/microsoft-365-business" },
          { logo: VENDOR_LOGOS.microsoft365, label: "Business Basic / Standard / Premium", href: "/microsoft-365-business#pricing" },
          { logo: VENDOR_LOGOS.microsoft365, label: "Business Standard + Copilot",   href: "/microsoft-365-business#pricing" },
          { logo: VENDOR_LOGOS.microsoft365, label: "Microsoft Teams Essentials",    href: "/microsoft-365-business#pricing" },
        ],
      },
      {
        title: "Microsoft 365 · Enterprise",
        items: [
          { logo: VENDOR_LOGOS.microsoft365, label: "Microsoft 365 Enterprise Plans", href: "/microsoft-365-enterprise" },
          { logo: VENDOR_LOGOS.microsoft365, label: "M365 E3 / E5",                   href: "/microsoft-365-enterprise#pricing" },
          { logo: VENDOR_LOGOS.microsoft365, label: "Microsoft Teams Enterprise",     href: "/microsoft-365-enterprise#pricing" },
          { logo: VENDOR_LOGOS.microsoft365, label: "M365 Apps for Enterprise",       href: "/microsoft-365-enterprise#pricing" },
        ],
      },
      {
        title: "Microsoft 365 · Frontline",
        items: [
          { logo: VENDOR_LOGOS.microsoft365, label: "Microsoft 365 Frontline Plans",  href: "/microsoft-365-frontline" },
          { logo: VENDOR_LOGOS.microsoft365, label: "M365 F1 (chat-focused)",         href: "/microsoft-365-frontline#pricing" },
          { logo: VENDOR_LOGOS.microsoft365, label: "M365 F3 (with mailbox)",         href: "/microsoft-365-frontline#pricing" },
          { logo: VENDOR_LOGOS.microsoft,    label: "Microsoft Entra ID P1 / P2",     href: "/products?brand=Microsoft%20Subscription&subcategory=Identity%20%26%20Access" },
        ],
      },
      {
        title: "Cloud Services",
        items: [
          { logo: VENDOR_LOGOS.microsoft, label: "Exchange Online Plan 1 / 2", href: "/products?brand=Microsoft%20Subscription&subcategory=Hosted%20Email" },
          { logo: VENDOR_LOGOS.microsoft, label: "Microsoft Intune Suite",     href: "/products?brand=Microsoft%20Subscription&subcategory=Endpoint%20Management" },
          { logo: VENDOR_LOGOS.microsoft, label: "Microsoft Teams · Power BI", href: "/products?brand=Microsoft%20Subscription&subcategory=Per%20User" },
          { logo: VENDOR_LOGOS.microsoft, label: "Planner · Visio · Project",  href: "/products?brand=Microsoft%20Subscription&subcategory=Diagramming" },
        ],
      },
    ],
    footer: { label: "View all Microsoft Subscription plans →", href: "/microsoft-subscriptions" },
  },
  msDynamics: {
    label: "MS Dynamics",
    logo: "/brand-logos/ms-dynamics-365.svg",
    href: "/microsoft-dynamics",
    columns: [
      {
        title: "Business Central",
        items: [
          { logo: VENDOR_LOGOS.microsoft, label: "Business Central Essentials", href: "/products?brand=Microsoft%20Dynamics&subcategory=Business%20Central" },
          { logo: VENDOR_LOGOS.microsoft, label: "Business Central Premium", href: "/products?brand=Microsoft%20Dynamics&subcategory=Business%20Central" },
          { logo: VENDOR_LOGOS.microsoft, label: "Business Central Team Members", href: "/products?brand=Microsoft%20Dynamics&subcategory=Business%20Central" },
        ],
      },
      {
        title: "Sales · Customer Service",
        items: [
          { logo: VENDOR_LOGOS.microsoft, label: "Sales Professional", href: "/products?brand=Microsoft%20Dynamics&subcategory=Sales" },
          { logo: VENDOR_LOGOS.microsoft, label: "Sales Enterprise / Premium", href: "/products?brand=Microsoft%20Dynamics&subcategory=Sales" },
          { logo: VENDOR_LOGOS.microsoft, label: "Customer Service Enterprise", href: "/products?brand=Microsoft%20Dynamics&subcategory=Customer%20Service" },
          { logo: VENDOR_LOGOS.microsoft, label: "Contact Center", href: "/products?brand=Microsoft%20Dynamics&subcategory=Customer%20Service" },
        ],
      },
      {
        title: "Finance & Operations",
        items: [
          { logo: VENDOR_LOGOS.microsoft, label: "Dynamics 365 Finance", href: "/products?brand=Microsoft%20Dynamics&subcategory=Finance%20%26%20Operations" },
          { logo: VENDOR_LOGOS.microsoft, label: "Supply Chain Management", href: "/products?brand=Microsoft%20Dynamics&subcategory=Finance%20%26%20Operations" },
          { logo: VENDOR_LOGOS.microsoft, label: "Field Service", href: "/products?brand=Microsoft%20Dynamics&subcategory=Operations" },
          { logo: VENDOR_LOGOS.microsoft, label: "Project Operations / HR / Commerce", href: "/products?brand=Microsoft%20Dynamics&subcategory=Operations" },
        ],
      },
    ],
    footer: { label: "Explore all Dynamics 365 modules →", href: "/microsoft-dynamics" },
  },
  adobe: {
    label: "Adobe",
    logo: VENDOR_LOGOS.adobe,
    href: "/adobe",
    columns: [
      {
        title: "Creative Cloud",
        items: [
          { logo: VENDOR_LOGOS.creativecloud, label: "CC Pro for Teams", href: "/adobe" },
          { logo: VENDOR_LOGOS.creativecloud, label: "CC Standard for Teams", href: "/adobe" },
          { logo: VENDOR_LOGOS.creativecloud, label: "CC Pro (Individual)", href: "/adobe" },
          { logo: VENDOR_LOGOS.creativecloud, label: "Photography Plan", href: "/adobe" },
        ],
      },
      {
        title: "Design & Video",
        items: [
          { logo: VENDOR_LOGOS.photoshop, label: "Photoshop", href: "/adobe" },
          { logo: VENDOR_LOGOS.illustrator, label: "Illustrator", href: "/adobe" },
          { logo: VENDOR_LOGOS.premierepro, label: "Premiere Pro", href: "/adobe" },
          { logo: `${I}/adobe-after-effects.png`, label: "After Effects", href: "/adobe" },
        ],
      },
      {
        title: "PDF & AI",
        items: [
          { logo: VENDOR_LOGOS.acrobat, label: "Acrobat Pro", href: "/adobe" },
          { logo: VENDOR_LOGOS.acrobat, label: "Acrobat Standard", href: "/adobe" },
          { logo: CC, label: "Adobe Firefly", href: "/adobe" },
          { logo: CC, label: "Adobe Express", href: "/adobe" },
        ],
      },
    ],
    footer: { label: "Browse all Adobe products →", href: "/adobe" },
  },
  autodesk: {
    label: "Autodesk",
    logo: VENDOR_LOGOS.autodesk,
    href: "/autodesk",
    columns: [
      {
        title: "Design & CAD",
        items: [
          { logo: VENDOR_LOGOS.autocad, label: "AutoCAD", href: "/autodesk" },
          { logo: VENDOR_LOGOS.autocad, label: "AutoCAD LT", href: "/autodesk" },
          { logo: VENDOR_LOGOS.revit, label: "Revit", href: "/autodesk" },
          { logo: VENDOR_LOGOS.revit, label: "Civil 3D", href: "/autodesk" },
        ],
      },
      {
        title: "Manufacturing",
        items: [
          { logo: VENDOR_LOGOS.fusion, label: "Fusion", href: "/autodesk" },
          { logo: VENDOR_LOGOS.fusion, label: "Inventor Pro", href: "/autodesk" },
          { logo: VENDOR_LOGOS.fusion, label: "Vault Pro", href: "/autodesk" },
          { logo: VENDOR_LOGOS.autodesk, label: "PDM Collection", href: "/autodesk" },
        ],
      },
      {
        title: "Media & Entertainment",
        items: [
          { logo: VENDOR_LOGOS.maya, label: "Maya", href: "/autodesk" },
          { logo: VENDOR_LOGOS.autodesk, label: "3ds Max", href: "/autodesk" },
          { logo: VENDOR_LOGOS.autodesk, label: "Arnold", href: "/autodesk" },
          { logo: VENDOR_LOGOS.autodesk, label: "M&E Collection", href: "/autodesk" },
        ],
      },
    ],
    footer: { label: "Browse all Autodesk products →", href: "/autodesk" },
  },
  cloud: {
    label: "Cloud",
    logo: VENDOR_LOGOS.azure,
    href: "/microsoft-azure",
    columns: [
      {
        title: "Microsoft Azure",
        items: [
          { logo: VENDOR_LOGOS.azure, label: "Azure Cloud Services", href: "/microsoft-azure" },
          { logo: VENDOR_LOGOS.azure, label: "Azure Reservations (up to 72%)", href: "/microsoft-azure#savings" },
          { logo: VENDOR_LOGOS.azure, label: "Azure Migration", href: "/microsoft-azure#migration" },
          { logo: VENDOR_LOGOS.azure, label: "PAYG Calculator", href: "/azure-calculator" },
        ],
      },
      {
        title: "Public Cloud",
        items: [
          { logo: VENDOR_LOGOS.aws, label: "AWS Managed", href: "/services" },
          { logo: VENDOR_LOGOS.gcp, label: "Google Cloud", href: "/services" },
          { logo: VENDOR_LOGOS.googleworkspace, label: "Google Workspace", href: "/services" },
          { logo: VENDOR_LOGOS.microsoft365, label: "M365 Migration", href: "/services" },
        ],
      },
      {
        title: "Cloud Services",
        items: [
          { logo: VENDOR_LOGOS.emailmigration, label: "Email Migration", href: "/services" },
          { logo: VENDOR_LOGOS.backup, label: "Backup & DR", href: "/services" },
          { logo: VENDOR_LOGOS.data, label: "Data Migration", href: "/services" },
          { logo: VENDOR_LOGOS.azure, label: "Azure Cost Optimization", href: "/services" },
        ],
      },
    ],
    footer: { label: "Explore cloud & managed services →", href: "/services" },
  },
  zoho: {
    label: "Zoho",
    logo: VENDOR_LOGOS.zoho,
    href: "/zoho",
    columns: [
      {
        title: "Bundles & Suites",
        items: [
          { logo: VENDOR_LOGOS.zoho, label: "Zoho One (all apps)", href: "/zoho?cat=bundle" },
          { logo: VENDOR_LOGOS.zoho, label: "Workplace bundle", href: "/zoho?cat=collab" },
          { logo: VENDOR_LOGOS.zoho, label: "All Zoho catalog", href: "/zoho" },
        ],
      },
      {
        title: "Mail & Collaboration",
        items: [
          { logo: VENDOR_LOGOS.zoho, label: "Mail", href: "/zoho?cat=mail" },
          { logo: VENDOR_LOGOS.zoho, label: "Workplace", href: "/zoho?cat=collab" },
          { logo: VENDOR_LOGOS.zoho, label: "Cliq / Meeting", href: "/zoho?cat=collab" },
          { logo: VENDOR_LOGOS.zoho, label: "Workdrive", href: "/zoho?cat=collab" },
        ],
      },
      {
        title: "Sales & Finance",
        items: [
          { logo: VENDOR_LOGOS.zoho, label: "CRM", href: "/zoho?cat=crm" },
          { logo: VENDOR_LOGOS.zoho, label: "Bigin (SMB CRM)", href: "/zoho?cat=crm" },
          { logo: VENDOR_LOGOS.zoho, label: "Books", href: "/zoho?cat=finance" },
          { logo: VENDOR_LOGOS.zoho, label: "Expense", href: "/zoho?cat=finance" },
        ],
      },
      {
        title: "Operations & IT",
        items: [
          { logo: VENDOR_LOGOS.zoho, label: "Desk", href: "/zoho?cat=desk" },
          { logo: VENDOR_LOGOS.zoho, label: "Projects", href: "/zoho?cat=projects" },
          { logo: VENDOR_LOGOS.zoho, label: "Assist", href: "/zoho?cat=itops" },
          { logo: VENDOR_LOGOS.zoho, label: "Analytics", href: "/zoho?cat=analytics" },
        ],
      },
    ],
    footer: { label: "Browse full Zoho catalog (Up to 15% off) →", href: "/zoho" },
  },
  tally: {
    label: "Tally",
    logo: VENDOR_LOGOS.tally,
    href: "/tally",
    columns: [
      {
        title: "TallyPrime Licences",
        items: [
          { logo: VENDOR_LOGOS.tally, label: "TallyPrime Silver (Single user)", href: "/products/tally-prime-silver" },
          { logo: VENDOR_LOGOS.tally, label: "TallyPrime Gold (Multi user)", href: "/products/tally-prime-gold" },
          { logo: VENDOR_LOGOS.tally, label: "Compare Silver vs Gold", href: "/tally" },
        ],
      },
      {
        title: "TSS Renewal",
        items: [
          { logo: VENDOR_LOGOS.tally, label: "Silver TSS · 1 year (₹4,500)", href: "/products/tally-tss-renewal" },
          { logo: VENDOR_LOGOS.tally, label: "Gold TSS · 1 year (₹13,500)", href: "/products/tally-tss-renewal" },
          { logo: VENDOR_LOGOS.tally, label: "Auditor TSS · 1 year (₹6,750)", href: "/products/tally-tss-renewal" },
          { logo: VENDOR_LOGOS.tally, label: "2-year TSS (save 10%)", href: "/products/tally-tss-renewal" },
        ],
      },
      {
        title: "Why India Enterprises",
        items: [
          { logo: VENDOR_LOGOS.tally, label: "Authorised Tally partner", href: "/tally" },
          { logo: VENDOR_LOGOS.tally, label: "Free onboarding + data migration", href: "/tally" },
          { logo: VENDOR_LOGOS.tally, label: "Auto TSS renewal reminders", href: "/tally" },
          { logo: VENDOR_LOGOS.tally, label: "Delhi NCR on-site support", href: "/tally" },
        ],
      },
    ],
    footer: { label: "Buy TallyPrime or renew TSS →", href: "/tally" },
  },
  quickheal: {
    label: "Quick Heal",
    logo: VENDOR_LOGOS.quickheal,
    href: "/quickheal",
    columns: [
      {
        title: "Total Security",
        items: [
          { logo: VENDOR_LOGOS.quickheal, label: "Total Security · 1 User · 1yr", href: "/products/quick-heal-total-security-qh-total-security" },
          { logo: VENDOR_LOGOS.quickheal, label: "Total Security · 5 Users · 1yr", href: "/products/quick-heal-total-security-qh-total-security" },
          { logo: VENDOR_LOGOS.quickheal, label: "Total Security · 10 Users · 1yr", href: "/products/quick-heal-total-security-qh-total-security" },
          { logo: VENDOR_LOGOS.quickheal, label: "All Total Security tiers", href: "/quickheal" },
        ],
      },
      {
        title: "Internet Security & AntiVirus",
        items: [
          { logo: VENDOR_LOGOS.quickheal, label: "Internet Security · 1 User", href: "/products/quick-heal-internet-security-qh-internet-security" },
          { logo: VENDOR_LOGOS.quickheal, label: "AntiVirus Pro · 1 User", href: "/products/quick-heal-antivirus-pro-qh-antivirus-pro" },
          { logo: VENDOR_LOGOS.quickheal, label: "AntiVirus for Server", href: "/products/quick-heal-antivirus-for-server-qh-server-antivirus" },
          { logo: VENDOR_LOGOS.quickheal, label: "Guardian Total Security", href: "/products/guardian-total-security-by-quick-heal-qh-guardian-total" },
        ],
      },
      {
        title: "Why India Enterprises",
        items: [
          { logo: VENDOR_LOGOS.quickheal, label: "Authorised Quick Heal partner", href: "/quickheal" },
          { logo: VENDOR_LOGOS.quickheal, label: "Free remote install", href: "/quickheal" },
          { logo: VENDOR_LOGOS.quickheal, label: "Renewal reminders", href: "/quickheal" },
          { logo: VENDOR_LOGOS.quickheal, label: "GST invoice + ITC", href: "/quickheal" },
        ],
      },
    ],
    footer: { label: "Browse the full Quick Heal catalog →", href: "/quickheal" },
  },
  seqrite: {
    label: "Seqrite",
    logo: VENDOR_LOGOS.seqrite,
    href: "/seqrite",
    columns: [
      {
        title: "SOHO Endpoint Security",
        items: [
          { logo: VENDOR_LOGOS.seqrite, label: "SOHO Total · 5 Users · 1yr", href: "/products/seqrite-soho-total-security-seqrite-soho-total" },
          { logo: VENDOR_LOGOS.seqrite, label: "SOHO Total · 10 Users · 1yr", href: "/products/seqrite-soho-total-security-seqrite-soho-total" },
          { logo: VENDOR_LOGOS.seqrite, label: "SOHO Total · 25 Users · 1yr", href: "/products/seqrite-soho-total-security-seqrite-soho-total" },
          { logo: VENDOR_LOGOS.seqrite, label: "All SOHO tiers", href: "/seqrite" },
        ],
      },
      {
        title: "Add-ons & Renewals",
        items: [
          { logo: VENDOR_LOGOS.seqrite, label: "SOHO Addon Licence", href: "/products/seqrite-soho-total-addon-license-seqrite-soho-addon" },
          { logo: VENDOR_LOGOS.seqrite, label: "SOHO Renewal", href: "/products/seqrite-soho-total-renewal-seqrite-soho-renewal" },
          { logo: VENDOR_LOGOS.seqrite, label: "SOHO Cloud Addon", href: "/products/seqrite-soho-total-cloud-addon-license-seqrite-soho-cloud-addon" },
          { logo: VENDOR_LOGOS.seqrite, label: "SOHO Cloud Renewal", href: "/products/seqrite-soho-total-cloud-renewal-seqrite-soho-cloud-renewal" },
        ],
      },
      {
        title: "Data Loss Prevention",
        items: [
          { logo: VENDOR_LOGOS.seqrite, label: "DLP On-Prem · per user", href: "/products/seqrite-data-loss-prevention-on-prem-seqrite-dlp-onprem" },
          { logo: VENDOR_LOGOS.seqrite, label: "DLP Cloud · per user", href: "/products/seqrite-data-loss-prevention-cloud-seqrite-dlp-cloud" },
          { logo: VENDOR_LOGOS.seqrite, label: "Encryption Manager", href: "/products/seqrite-encryption-manager-bitlocker-filevault-seqrite-encryption-manager" },
          { logo: VENDOR_LOGOS.seqrite, label: "Authorised Seqrite partner", href: "/seqrite" },
        ],
      },
      {
        title: "Enterprise Security",
        items: [
          { logo: VENDOR_LOGOS.seqrite, label: "Endpoint Security (EPS)", href: "/products/seqrite-endpoint-security-eps-seqrite-eps" },
          { logo: VENDOR_LOGOS.seqrite, label: "Terminator UTM (Firewall)", href: "/products/seqrite-terminator-utm-firewall-appliance-seqrite-utm-terminator" },
          { logo: VENDOR_LOGOS.seqrite, label: "mSuite MDM (Mobile)", href: "/products/seqrite-msuite-mobile-device-management-seqrite-msuite-mdm" },
          { logo: VENDOR_LOGOS.seqrite, label: "Email Security (Gateway)", href: "/products/seqrite-email-security-cloud-gateway-seqrite-email-security" },
          { logo: VENDOR_LOGOS.seqrite, label: "Network Access Control", href: "/products/seqrite-network-access-control-nac-seqrite-nac" },
        ],
      },
    ],
    footer: { label: "Browse the full Seqrite catalog →", href: "/seqrite" },
  },
  chaos: {
    label: "Chaos",
    logo: VENDOR_LOGOS.chaos,
    href: "/chaos",
    columns: [
      {
        title: "V-Ray",
        items: [
          { logo: VENDOR_LOGOS.chaos, label: "V-Ray Suite — Solo · Premium · Collection", href: "/chaos/v-ray-suite" },
          { logo: VENDOR_LOGOS.chaos, label: "V-Ray Render Nodes — Monthly & 1/5/10/20/50/100-pack",  href: "/chaos/v-ray-render-nodes" },
          { logo: VENDOR_LOGOS.chaos, label: "V-Ray Education — Student · University seat bands", href: "/chaos/v-ray-education" },
        ],
      },
      {
        title: "Corona · Enscape",
        items: [
          { logo: VENDOR_LOGOS.chaos, label: "Corona Suite — Solo · Premium · Collection", href: "/chaos/corona-suite" },
          { logo: VENDOR_LOGOS.chaos, label: "Corona Render Nodes — Monthly · Annual · 3-Year", href: "/chaos/corona-render-nodes" },
          { logo: VENDOR_LOGOS.chaos, label: "Enscape Suite — Solo · Premium · Collection", href: "/chaos/enscape-suite" },
          { logo: VENDOR_LOGOS.chaos, label: "Enscape Education — Student · University", href: "/chaos/enscape-education" },
        ],
      },
      {
        title: "Evolvelab · Anima · Credits",
        items: [
          { logo: VENDOR_LOGOS.chaos, label: "Veras (Evolvelab) — AI photoreal images",     href: "/chaos/veras" },
          { logo: VENDOR_LOGOS.chaos, label: "Glyph (Evolvelab) — AI Revit families",       href: "/chaos/glyph" },
          { logo: VENDOR_LOGOS.chaos, label: "Morphis (Evolvelab) — Parametric massing",    href: "/chaos/morphis" },
          { logo: VENDOR_LOGOS.chaos, label: "Helix (Evolvelab) — Revit templates",         href: "/chaos/helix" },
          { logo: VENDOR_LOGOS.chaos, label: "Chaos Anima — Crowd sim",                     href: "/chaos/anima" },
          { logo: VENDOR_LOGOS.chaos, label: "Chaos Credits — Monthly · Annual pool",       href: "/chaos/credits" },
        ],
      },
      {
        title: "Why Buy From Us",
        items: [
          { logo: VENDOR_LOGOS.chaos, label: "Authorised Chaos reseller · India",   href: "/chaos" },
          { logo: VENDOR_LOGOS.chaos, label: "Best MRP · match any partner quote",  href: "/chaos" },
          { logo: VENDOR_LOGOS.chaos, label: "GST invoice · Same-day activation",   href: "/chaos" },
        ],
      },
    ],
    footer: { label: "Browse all Chaos product families →", href: "/chaos" },
  },
  sketchup: {
    label: "SketchUp",
    logo: VENDOR_LOGOS.sketchup,
    href: "/sketchup",
    columns: [
      {
        title: "SketchUp Plans",
        items: [
          { logo: VENDOR_LOGOS.sketchup, label: "SketchUp Go (Web + iPad)", href: "/sketchup?cat=Go" },
          { logo: VENDOR_LOGOS.sketchup, label: "SketchUp Pro (Windows/Mac)", href: "/sketchup?cat=Pro" },
          { logo: VENDOR_LOGOS.sketchup, label: "SketchUp Studio", href: "/sketchup?cat=Studio" },
          { logo: VENDOR_LOGOS.sketchup, label: "All SketchUp plans", href: "/sketchup" },
        ],
      },
      {
        title: "Specialised Editions",
        items: [
          { logo: VENDOR_LOGOS.sketchup, label: "Pro Scan (point-cloud)", href: "/sketchup?cat=Pro%20Scan" },
          { logo: VENDOR_LOGOS.sketchup, label: "Pro Civil Contractor", href: "/sketchup?cat=Pro%20Civil%20Contractor" },
          { logo: VENDOR_LOGOS.sketchup, label: "Pro Advanced Workflows", href: "/sketchup?cat=Pro%20Advanced%20Workflows" },
          { logo: VENDOR_LOGOS.sketchup, label: "Studio for Educators", href: "/sketchup?cat=For%20Educators" },
        ],
      },
      {
        title: "Why India Enterprises",
        items: [
          { logo: VENDOR_LOGOS.sketchup, label: "Authorised Trimble reseller", href: "/sketchup" },
          { logo: VENDOR_LOGOS.sketchup, label: "Up to 30% partner discount", href: "/sketchup" },
          { logo: VENDOR_LOGOS.sketchup, label: "GST invoice · pan-India", href: "/sketchup" },
          { logo: VENDOR_LOGOS.sketchup, label: "Instant ESD delivery", href: "/sketchup" },
        ],
      },
    ],
    footer: { label: "Browse all SketchUp plans →", href: "/sketchup" },
  },
  hardware: {
    label: "Hardware",
    logo: VENDOR_LOGOS.hpe,
    href: "/hardware",
    columns: [
      {
        title: "HPE Tower Servers",
        items: [
          { logo: VENDOR_LOGOS.hpe, label: "ProLiant MicroServer Gen10+", href: "/hardware/configure/hpe-microserver-gen10plus-v2" },
          { logo: VENDOR_LOGOS.hpe, label: "ProLiant ML30 Gen11", href: "/hardware/configure/hpe-ml30-gen11" },
          { logo: VENDOR_LOGOS.hpe, label: "ProLiant ML110 Gen11", href: "/hardware/configure/hpe-ml110-gen11" },
          { logo: VENDOR_LOGOS.hpe, label: "ProLiant ML350 Gen11", href: "/hardware/configure/hpe-ml350-gen11" },
        ],
      },
      {
        title: "HPE Rack Servers (Intel)",
        items: [
          { logo: VENDOR_LOGOS.hpe, label: "ProLiant DL20 Gen11 (1U Entry)", href: "/hardware/configure/hpe-dl20-gen11" },
          { logo: VENDOR_LOGOS.hpe, label: "ProLiant DL360 Gen11 (1U Popular)", href: "/hardware/configure/hpe-dl360-gen11" },
          { logo: VENDOR_LOGOS.hpe, label: "ProLiant DL380 Gen11 (2U Flagship)", href: "/hardware/configure/hpe-dl380-gen11" },
        ],
      },
      {
        title: "HPE Rack Servers (AMD)",
        items: [
          { logo: VENDOR_LOGOS.hpe, label: "ProLiant DL325 Gen11 (1U AMD EPYC)", href: "/hardware/configure/hpe-dl325-gen11" },
          { logo: VENDOR_LOGOS.hpe, label: "ProLiant DL385 Gen11 (2U AMD EPYC)", href: "/hardware/configure/hpe-dl385-gen11" },
          { logo: VENDOR_LOGOS.hpe, label: "Browse all HPE servers", href: "/hardware" },
        ],
      },
    ],
    footer: { label: "Configure any HPE server online — get a formal quote →", href: "/hardware" },
  },
  managed: {
    label: "Managed Services",
    logo: VENDOR_LOGOS.helpdesk,
    href: "/managed-services",
    variant: "tiered",
    intro: {
      title: "Managed IT Support",
      desc: "Let us manage your IT so you can focus on your business.",
      icon: VENDOR_LOGOS.helpdesk,
    },
    tiers: [
      {
        key: "silver",
        name: "Silver Support",
        subtitle: "Managed IT support for startups with 1–20 staff.",
        badge: "silver",
        accent: "from-slate-100 to-slate-200 border-slate-300",
        icon: `${I}/medal2.png`,
        features: ["Remote Support", "Printer & Scanner", "Firewall & IT Infra", "Software Installation", "Laptop & Desktop"],
        href: "/managed-services",
      },
      {
        key: "gold",
        name: "Gold Support",
        subtitle: "Best suited for small–medium businesses with 1–100 staff.",
        badge: "gold",
        accent: "from-amber-50 to-yellow-100 border-amber-300",
        icon: `${I}/medal.png`,
        features: ["On-site & Remote Support", "Printer & Scanner", "Firewall & IT Infra", "Software Installation", "Laptop & Desktop"],
        href: "/managed-services",
      },
      {
        key: "diamond",
        name: "Diamond Support",
        subtitle: "Best for companies looking for dedicated IT support staff.",
        badge: "diamond",
        accent: "from-indigo-100 to-blue-200 border-indigo-300",
        icon: `${I}/diamond.png`,
        features: ["Everything in Gold", "On-site & Remote Support", "Printer & Scanner", "Laptop & Desktop", "Dedicated On-site IT Expert"],
        href: "/managed-services",
      },
    ],
    subcategories: [
      {
        icon: `${I}/settings.png`,
        title: "Workplace Modernization",
        subtitle: "Digital Workplace",
        items: ["Migrate to Cloud", "Change Management", "WFH Solutions", "Cloud Cost Management"],
        href: "/services",
      },
      {
        icon: VENDOR_LOGOS.network,
        title: "Networking",
        subtitle: "Networking Solutions",
        items: ["WiFi Solution", "IT Support", "Structured Cabling", "New Office IT Setup"],
        href: "/services",
      },
      {
        icon: VENDOR_LOGOS.cybersecurity,
        title: "IT Security Solutions",
        subtitle: "Cyber Security",
        items: ["Managed Firewall Services", "Endpoint Security Solutions", "Data Loss Prevention", "Disaster Recovery & Backup"],
        href: "/services",
      },
      {
        icon: `${I}/video-call.png`,
        title: "Unified Communication",
        subtitle: "Video Conferencing",
        items: ["Polycom", "Microsoft Teams", "Zoom for Business", "Polycom for Teams"],
        href: "/services",
      },
    ],
    columns: [], // legacy fallback (unused in tiered variant)
    footer: { label: "Explore all managed services →", href: "/managed-services" },
  },

  // Resources — Price Lists / Buyer's Guides / Industry landings / Blog.
  // Groups the 22 keyword-gap SEO landings + the 5-post blog behind a
  // single header entry so human visitors can browse them without
  // hunting through /sitemap.
  resources: {
    label: "Resources",
    logo: `${I}/faq.png`,
    href: "/resources/blog",
    columns: [
      {
        title: "📝 Blog & Guides",
        items: [
          { logo: `${I}/faq.png`, label: "All Articles", href: "/resources/blog" },
          { logo: VENDOR_LOGOS.microsoft365, label: "Microsoft 365 Plans Compared", href: "/resources/blog/microsoft-365-business-basic-vs-standard-vs-premium" },
          { logo: `${I}/autocad.png`, label: "AutoCAD Price India 2026", href: "/resources/blog/autocad-price-india-2026" },
          { logo: VENDOR_LOGOS.creativecloud, label: "Buy Genuine Adobe License", href: "/resources/blog/how-to-buy-genuine-adobe-creative-cloud-license-india" },
          { logo: VENDOR_LOGOS.windows, label: "Windows Server 2025 vs 2022", href: "/resources/blog/windows-server-2025-vs-2022-should-you-upgrade" },
          { logo: VENDOR_LOGOS.zoho, label: "Zoho One vs Individual Apps", href: "/resources/blog/zoho-one-vs-individual-apps-india" },
        ],
      },
      {
        title: "💰 India Price Lists",
        items: [
          { logo: VENDOR_LOGOS.windows, label: "Windows Server", href: "/price-list/windows-server" },
          { logo: VENDOR_LOGOS.office, label: "Office LTSC 2024", href: "/price-list/microsoft-office-ltsc-2024" },
          { logo: VENDOR_LOGOS.microsoft365, label: "Microsoft 365", href: "/price-list/microsoft-365" },
          { logo: VENDOR_LOGOS.sqlserver, label: "SQL Server", href: "/price-list/sql-server" },
          { logo: `${I}/autocad.png`, label: "AutoCAD", href: "/price-list/autocad" },
          { logo: VENDOR_LOGOS.creativecloud, label: "Adobe Creative Cloud", href: "/price-list/adobe-creative-cloud" },
          { logo: VENDOR_LOGOS.corel, label: "CorelDRAW", href: "/price-list/coreldraw" },
          { logo: VENDOR_LOGOS.sketchup, label: "SketchUp Pro", href: "/price-list/sketchup-pro" },
          { logo: VENDOR_LOGOS.hpe, label: "HPE ProLiant", href: "/price-list/hpe-proliant" },
          { logo: VENDOR_LOGOS.zoho, label: "Zoho One", href: "/price-list/zoho-one" },
        ],
      },
      {
        title: "📘 Buyer's Guides & Industries",
        items: [
          { logo: `${I}/faq.png`, label: "CSP vs ESD vs Open Value", href: "/guides/csp-vs-esd-vs-open-value" },
          { logo: `${I}/faq.png`, label: "OEM vs Retail vs Volume", href: "/guides/oem-vs-retail-vs-volume" },
          { logo: `${I}/faq.png`, label: "Perpetual vs Subscription", href: "/guides/perpetual-vs-subscription" },
          { logo: `${I}/faq.png`, label: "User CAL vs Device CAL", href: "/guides/user-cal-vs-device-cal" },
          { logo: `${I}/faq.png`, label: "HPE Server Sizing", href: "/guides/hpe-server-sizing" },
          { logo: `${I}/small-business.png`, label: "SMB", href: "/industries/smb" },
          { logo: `${I}/organization.png`, label: "Enterprise", href: "/industries/enterprise" },
          { logo: `${I}/student-male.png`, label: "Education", href: "/industries/education" },
          { logo: `${I}/factory.png`, label: "Manufacturing", href: "/industries/manufacturing" },
        ],
      },
    ],
    footer: { label: "Read the India Enterprises blog →", href: "/resources/blog" },
  },
};
